import Service, { inject as service } from '@ember/service';
import { tracked, TrackedObject } from 'tracked-built-ins';

import { UNLIMITED_QUANTITY } from 'later/utils/constants';
import { fetch as laterFetch } from 'later/utils/fetch';
import { TagType, TagTypeMap } from 'shared/types/post-tagging';
import { convertApiTagToTag } from 'shared/utils/post-tagging/post-tagging';
import randomHexColor from 'shared/utils/random-hex-color';

import type { EmberClassArguments } from '@ember/object/-private/types';
import type IntlService from 'ember-intl/services/intl';
import type SubscriptionPlanModel from 'later/models/subscription-plan';
import type { AlertMessage } from 'later/services/alerts';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type DialogManagerService from 'later/services/dialog-manager';
import type ErrorsService from 'later/services/errors';
import type SubscriptionsService from 'later/services/subscriptions';
import type {
  Tag,
  ApiTag,
  NewTagParams,
  PostTagRequestItem,
  PostTagRequestResponse,
  TagLimit
} from 'shared/types/post-tagging';

export default class TagManagerService extends Service {
  @service declare alerts: AlertsService;
  @service declare auth: AuthService;
  @service declare subscriptions: SubscriptionsService;
  @service declare dialogManager: DialogManagerService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;

  @tracked tagsByGroup: Record<string, Record<string, Tag>> = new TrackedObject({});

  constructor(args: EmberClassArguments) {
    super(args);
    // Initialize the tracked objects for the current group
    if (this.auth.currentGroup?.id) {
      this.#ensureGroupData(this.auth.currentGroup.id);
    }
  }

  get currentSubscriptionPlan(): SubscriptionPlanModel | Record<string, never> {
    return this.subscriptions.subscriptionPlan;
  }

  get groupId(): string {
    return this.auth.currentGroup.id;
  }

  get tags(): Record<string, Tag> {
    return this.tagsByGroup[this.groupId] || {};
  }

  get tagsArray(): Tag[] {
    return Object.values(this.tags);
  }

  get productTags(): Tag[] {
    return this.tagsArray.filter((t) => t.tagType === TagType.Product);
  }

  get campaignTags(): Tag[] {
    return this.tagsArray.filter((t) => t.tagType === TagType.Campaign);
  }

  get tagLimits(): Record<TagType, TagLimit> {
    const { product, campaign } = this.currentSubscriptionPlan.postTaggingQuantities || { product: 0, campaign: 0 };

    return {
      [TagType.Product]: {
        allowed: product ?? 0,
        current: this.productTags.length,
        hasUnlimited: product === UNLIMITED_QUANTITY,
        isOverLimit: product !== UNLIMITED_QUANTITY && this.productTags.length > product,
        isAtLimit: product !== UNLIMITED_QUANTITY && this.productTags.length === product
      },
      [TagType.Campaign]: {
        allowed: campaign ?? 0,
        current: this.campaignTags.length,
        hasUnlimited: campaign === UNLIMITED_QUANTITY,
        isOverLimit: campaign !== UNLIMITED_QUANTITY && this.campaignTags.length > campaign,
        isAtLimit: campaign !== UNLIMITED_QUANTITY && this.campaignTags.length === campaign
      }
    };
  }

  async getTags(): Promise<Tag[]> {
    try {
      if (this.tagsArray.length > 0) {
        return this.tagsArray;
      }

      const url = `/api/v2/tags?group_id=${this.groupId}`;
      const response: { tags: ApiTag[] } = await laterFetch(url, { method: 'GET' });

      this.#ensureGroupData(this.groupId);

      response.tags.forEach((tag) => {
        this.tags[tag.id] = convertApiTagToTag(tag);
      });

      return this.tagsArray;
    } catch (error) {
      this.errors.log(error);
      this.#showAlert('get_tags');
      return [];
    }
  }

  async getTagsForPost(id: string, platform: string): Promise<Tag[]> {
    try {
      const url = `/api/v2/tags/for_post?group_id=${this.groupId}&post_id=${id}&platform=${platform}`;
      const response: { tags: ApiTag[] } = await laterFetch(url, { method: 'GET' });

      return response.tags.map((tag) => convertApiTagToTag(tag));
    } catch (error) {
      this.errors.log(error);
      this.#showAlert('get_tags');
      return [];
    }
  }

  async createTag(tagType: TagType, name: string): Promise<Tag | undefined> {
    try {
      const newTag: NewTagParams = {
        name,
        tag_type: TagTypeMap[tagType],
        color: randomHexColor(),
        group_id: this.groupId
      };
      this.tags.temp = convertApiTagToTag({ ...newTag, id: 'temp', data: { color: newTag.color } });

      const response: { tag: ApiTag } = await laterFetch('/api/v2/tags', {
        method: 'POST',
        body: JSON.stringify(newTag)
      });

      if (response.tag) {
        const newTag = convertApiTagToTag(response.tag);
        delete this.tags.temp;
        this.tags[response.tag.id] = newTag;

        return newTag;
      }
      delete this.tags.temp;
    } catch (error) {
      delete this.tags.temp;
      this.errors.log(error);
      this.#showAlert('add_tag');
    }
    return undefined;
  }

  async deleteTag(tagId: string): Promise<Tag | undefined> {
    try {
      const tag = this.tags[tagId];

      if (tag) {
        const confirmedDeletion = await this.dialogManager.confirmation(
          this.intl.t('post-tagging.dialog.delete_tag.title'),
          {
            description: this.intl.t('post-tagging.dialog.delete_tag.description'),
            confirmButton: this.intl.t('post-tagging.dialog.delete_tag.confirm')
          }
        );

        if (!confirmedDeletion) {
          return undefined;
        }

        await laterFetch(`/api/v2/tags/${tagId}`, { method: 'DELETE' });

        delete this.tags[tagId];

        return tag;
      }
    } catch (error) {
      this.errors.log(error);
      this.#showAlert('delete_tag');
    }
    return undefined;
  }

  async processPostTagRequest(
    endpoint: 'tag_posts' | 'untag_posts',
    postTags: PostTagRequestItem[]
  ): Promise<ApiTag[] | undefined> {
    try {
      const body = JSON.stringify({
        post_tags: postTags,
        group_id: this.groupId
      });

      const result: PostTagRequestResponse = await laterFetch(`/api/v2/post_tagging/${endpoint}`, {
        method: 'POST',
        body
      });

      if (result.failed_post_tags?.length) {
        this.errors.log('Error processing post tags', {
          action: endpoint,
          failed_post_tags: result.failed_post_tags,
          group_id: this.groupId
        });
        this.#showAlert('partial_process_post_tag', 'warning');
      }

      return result.post_tags;
    } catch (error) {
      this.errors.log('Error processing post tags', { action: endpoint, error, group_id: this.groupId });
      this.#showAlert('process_post_tag');
      return undefined;
    }
  }

  async updateTag(updatedTag: Tag): Promise<boolean> {
    const tempTag = this.tags[updatedTag.id];
    try {
      const nameAlreadyExists = Object.values(this.tags).some(
        (t) => t.name.toLowerCase() === updatedTag.name.toLowerCase()
      );
      if (nameAlreadyExists) return false;

      this.tags[updatedTag.id] = updatedTag;

      const response: { tag: ApiTag } = await laterFetch(`/api/v2/tags/${updatedTag.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: updatedTag.name }) // ! NOTE: Currently only supports name change
      });

      if (!response.tag) {
        this.tags[updatedTag.id] = tempTag;
        return false;
      }

      return true;
    } catch (error) {
      this.tags[updatedTag.id] = tempTag;

      this.errors.log(error);
      this.#showAlert('update_tag');
      return false;
    }
  }

  #ensureGroupData(groupId: string): void {
    this.tagsByGroup[groupId] = this.tagsByGroup[groupId] || new TrackedObject({});
  }

  #showAlert(translationKey: string, type: AlertMessage['type'] = 'alert'): void {
    const description = this.intl.t(`post-tagging.errors.${translationKey}.description`);
    const title = this.intl.t(`post-tagging.errors.${translationKey}.title`);

    if (type === 'warning') {
      this.alerts.warning(description, {
        title
      });
    } else {
      this.alerts.alert(description, {
        title
      });
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'post-tagging/tag-manager': TagManagerService;
  }
}
